import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 160px 0 100px;
  text-align: center;
  color: #1e293b;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(43, 75, 140, 0.05) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 20px;
  letter-spacing: -1px;
  position: relative;
  z-index: 1;
  color: #0f172a;

  @media (max-width: 768px) { font-size: 2.8rem; }
`;

const HeroDesc = styled.p`
  font-size: 1.3rem;
  color: #475569;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) { font-size: 1.1rem; }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: -60px auto 80px;
  padding: 0 5%;
  position: relative;
  z-index: 2;
`;

const ContentGrid = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 50px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const InfoItemsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  @media (max-width: 768px) { grid-template-columns: 1fr; }
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const InfoTitle = styled.h2`
  font-size: 2rem;
  color: #1e293b;
  margin-bottom: 10px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;

  .icon-wrapper {
    width: 54px;
    height: 54px;
    background: #f1f5f9;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  &:hover .icon-wrapper {
    background: #2B4B8C;
    color: white;
    transform: scale(1.05);
  }

  &:hover .icon-wrapper svg {
    stroke: white;
    fill: white;
  }

  .details {
    flex: 1;

    h3 {
      font-size: 1.1rem;
      color: #334155;
      margin-bottom: 5px;
      font-weight: 600;
    }

    p, a {
      color: #64748b;
      font-size: 1.05rem;
      line-height: 1.5;
      text-decoration: none;
      transition: color 0.2s;
    }

    a:hover {
      color: #2B4B8C;
    }
  }
`;

const MapWrapper = styled.div`
  margin-top: 40px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  height: 450px;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  iframe {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const PhoneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const MailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

const Contact = () => {
  const { t } = useLanguage();

  return (
    <>
      <HeroSection>
        <HeroTitle>{t('contact.title')}</HeroTitle>
        <HeroDesc>{t('contact.heroDesc')}</HeroDesc>
      </HeroSection>

      <Container>
        <ContentGrid>
          <InfoCard>
            <InfoTitle style={{textAlign: "center", marginBottom: "10px"}}>{t('contact.infoTitle')}</InfoTitle>
            
            <InfoItemsContainer>
              <InfoItem>
                <div className="icon-wrapper"><PhoneIcon /></div>
                <div className="details">
                  <h3>{t('contact.phone')}</h3>
                  <a href="tel:+905321316330"><bdi>0532 131 63 30</bdi></a>
                </div>
              </InfoItem>

              <InfoItem>
                <div className="icon-wrapper"><MailIcon /></div>
                <div className="details">
                  <h3>{t('contact.email')}</h3>
                  <a href="mailto:info@dmrcimento.com">info@dmrcimento.com</a>
                </div>
              </InfoItem>

              <InfoItem>
                <div className="icon-wrapper"><ClockIcon /></div>
                <div className="details">
                  <h3>{t('contact.workingHours')}</h3>
                  <p>{t('contact.weekdays')}<br/><bdi>08:00 - 18:00</bdi></p>
                </div>
              </InfoItem>
            </InfoItemsContainer>
          </InfoCard>
        </ContentGrid>

        <MapWrapper>
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4059.2725139056674!2d38.65013388213027!3d37.118842843043915!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1534756e29a3aafb%3A0xe8eb64ff984be3d1!2sDMR%20%C3%87imento!5e1!3m2!1str!2str!4v1784759041346!5m2!1str!2str" 
            width="100%" 
            height="100%" 
            style={{border:0}} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="DMR Çimento Harita"
          ></iframe>
        </MapWrapper>
      </Container>
    </>
  );
};

export default Contact;
